import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { PopulateFormService } from '../../services/populate-form';
import { OidcSecurityService } from '../../auth/services/oidc.security.service';
import { SheetService } from '../../shared/sheet-export-import/sheet.service';
import { MULTICOLOR_FULL, BLUE_FULL, DOTS_FULL, PreloaderHandler } from '../../shared/preloader/preloader-handler';
import { CheckboxModel } from '../../shared/models/CheckboxModel';
import { CompanyListModel } from '../../shared/models/company-related/CompanyListModel';
import { NotificationHandlerService, ReportService, LoadingService } from '../../index';
import { UnitReportSearchParamModel } from '../../shared/models/search-related/UnitReportSearchParamModel';
import { UnitSimReportModel } from '../../shared/models/UnitSimReportModel';
import { ImportColumnCheckboxModel } from '../../shared/models/ImportColumnCheckboxModel';
import { forkJoin } from 'rxjs/observable/forkJoin';

@Injectable()
export class ReportUnitBackgroundService {

    private MY_NAME = 'REPORT_UNIT_BG_SERVICE';

    // Unit types & array of all types
    private UNIT_NEW = 'New';
    private UNIT_ACTIVE = 'Active';
    private UNIT_INACTIVE = 'Inactive';
    private UNIT_CANCELLED = 'Cancelled';
    private allReportTypes: string[];

    // Used to remember selection when running long reports
    // This data is broadcast back from sheet service
    private rememberCols = false;
    private rememberSelectedColumns: ImportColumnCheckboxModel[];

    // Used to allow subscribing from parent component to show when a report is running
    public isRunningReports = new BehaviorSubject<boolean>(false);

    // All selected report sheets (passed when starting report)
    private selectedSheets: CheckboxModel[];

    // All companies to run report against
    private reportCompanies: CompanyListModel[];

    // Used to identify selected reports
    private currentReportSheets: CheckboxModel[];
    private currentReportNames: string[];
    // The current company which reports are running on
    private currentReportCompany: CompanyListModel;

    // Start and end date for query
    private startDate: Date;
    private endDate: Date;

    // Report name and returned data
    private reportName: string;
    private reportData: any = [];

    // Ised to identify whether the current report has been cancelled
    private isCancelled: boolean;

    constructor(private _sheetService: SheetService,
        private _notificationService: NotificationHandlerService,
        private _loadingService: LoadingService,
        private _reportService: ReportService,
        private _pfService: PopulateFormService,
        public oidcSecurityService: OidcSecurityService) {
        this.isCancelled = false;
        this.allReportTypes = [
            this.UNIT_ACTIVE,
            this.UNIT_INACTIVE,
            this.UNIT_CANCELLED,
            this.UNIT_NEW
        ];

        this._sheetService.sheetColumnSelection.subscribe((data) => {
            if (data instanceof Array) {
                // Set broadcasted parameters
                // 0 -- Remember selection of columns
                // 1 -- All selected column objects
                // 2 -- Whether the user cancelled report from column select
                this.rememberCols = data[0];
                this.rememberSelectedColumns = data[1];
                //If user clicked cancel
                if (data[2]) {
                    // Cancel report
                    this.reportCancelled();
                } else {
                    // Generate report
                    if (data[3] == this.MY_NAME) {
                        this.generateReport();
                    }
                }
            }
        });
    }

    // Broabcast the running status of the report
    private updateRunningStatus(status: boolean): void {
        this.isRunningReports.next(status);
    }

    // Setup variables used to run the reports & start the report
    public readyReport(selectedSheets: CheckboxModel[], companies: CompanyListModel[], startDate: Date, endDate: Date): void {
        this.isCancelled = false;
        this.selectedSheets = selectedSheets;
        this.reportCompanies = companies.reverse();
        this.startDate = startDate;
        this.endDate = endDate;
        this.updateRunningStatus(true);
        this._loadingService.itemLoading();
        this.runReport();
    }

    // Cancel current running report
    public reportCancelled(): void {
        this._notificationService.logToastWarning('Report cancelled...');
        this.isCancelled = true;
        this.resetReportProperties();
    }

    // Current report has completed
    public reportComplete(): void {
        this._notificationService.logToastSuccess('Report complete...');
        this.resetReportProperties();
    }

    // Reset used variables to default after completing or cancelling
    private resetReportProperties(): void {
        this.updateRunningStatus(false);
        this._loadingService.itemLoaded();
        this.selectedSheets = [];
        this.reportCompanies = [];
    }

    // Grab a company out the list and start runnning report on it
    private runReport(): void {
        this.currentReportSheets = this.selectedSheets.filter(sheet => sheet.value === true);
        this.currentReportCompany = this.reportCompanies.pop();
        this._notificationService.logToastInfo('Running report on ' + this.currentReportCompany.CompanyName + '...');
        this.getNextSheets();
    }

    // Get all selected reports from the api, wait for them to complete then continue
    private getNextSheets(): void {
        if (!this.isCancelled) {
            let reportPromises = [];
            this.currentReportNames = [];
            if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_ACTIVE)) {
                reportPromises.push(this._reportService.GetActiveFullReport(this.currentReportCompany.CompanyId));
                this.currentReportNames.push(this.UNIT_ACTIVE);
            }
            if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_INACTIVE)) {
                reportPromises.push(this._reportService.GetInactiveDatedReport(this.currentReportCompany.CompanyId, this.startDate, this.endDate));
                this.currentReportNames.push(this.UNIT_INACTIVE);
            }
            if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_CANCELLED)) {
                reportPromises.push(this._reportService.GetCancelledDatedReport(this.currentReportCompany.CompanyId, this.startDate, this.endDate));
                this.currentReportNames.push(this.UNIT_CANCELLED);
            }
            if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_NEW)) {
                reportPromises.push(this._reportService.GetActiveDatedReport(this.currentReportCompany.CompanyId, this.startDate, this.endDate));
                this.currentReportNames.push(this.UNIT_NEW);
            }

            forkJoin(reportPromises)
                .subscribe((data) => {
                    this.reportData = data;
                    this.getUserSelection();
                }, (error) => {
                    this._notificationService.logError(error);
                });
        }
    }

    // Prompt user and get selection of columns
    private getUserSelection(): void {
        if (this.rememberCols) {
            this.generateReport();
        } else {
            let dummy = this.getDummyModel();
            // Pass a dummy object to column select component incase returned data has no objects
            // Pass a blank sheet name as we expect the selcted columns to com back before being added
            // Pass true, indicating we want the selection to be broadcast back instead of automatically adding sheet
            this._sheetService.promptColumnSelect(dummy, '', true, this.MY_NAME);
        }
    }

    // Get the next company out the list and start report
    private getNextCompany(): void {
        if (this.reportCompanies.length > 0) {
            this.runReport();
        } else {
            this.reportComplete();
        }
    }

    // Add all data to new sheets then initiate workbook download
    private generateReport(): void {
        if (!this.isCancelled) {
            let reports = [];
            //if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_ACTIVE)) {
            //    reports.push(this._sheetService.addSheet(this.reportData[0], this.rememberSelectedColumns, this.allReportTypes[0]));
            //}
            //if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_INACTIVE)) {
            //    reports.push(this._sheetService.addSheet(this.reportData[1], this.rememberSelectedColumns, this.allReportTypes[1]));
            //}
            //if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_CANCELLED)) {
            //    reports.push(this._sheetService.addSheet(this.reportData[2], this.rememberSelectedColumns, this.allReportTypes[2]));
            //}
            //if (this.currentReportSheets.some(sheet => sheet.name == this.UNIT_NEW)) {
            //    reports.push(this._sheetService.addSheet(this.reportData[3], this.rememberSelectedColumns, this.allReportTypes[3]));
            //}

            for (let i = 0; i < this.currentReportNames.length; i++) {
                reports.push(this._sheetService.addSheet(this.reportData[i], this.rememberSelectedColumns, this.currentReportNames[i]));
            }

            Promise.all(reports).then(() => {
                this.downloadReport().then(() => {
                    this.getNextCompany();
                });
            });
        }
    }

    // Download workbook then check if there are any more companies
    private downloadReport(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this._sheetService.downloadWorkbook(this.getReportName()).then(() => {
                resolve(true);
            });
        });
    }

    // Get report name from current company and report date
    private getReportName(): string {
        let name = this.currentReportCompany.CompanyName.replace(' ', '_') + '_' + this.getDateStr(this.endDate);
        return name;
    }

    // Convert a date object to a month name and full year string
    private getDateStr(date: Date): string {
        date = new Date(date);
        let months = [' Jan ', ' Feb ', ' Mar ', ' Apr ', ' May ', ' Jun ',
            ' Jul ', ' Aug ', ' Sep ', ' Oct ', ' Nov ', ' Dec '];
        return (months[date.getMonth()].toUpperCase() + date.getFullYear());
    }

    // Return a model of report objects with dummy data
    private getDummyModel(): UnitSimReportModel[] {
        let dummyUnit: UnitSimReportModel = {
            UnitId: '',
            Product: '',
            MonitoringCentre: '',
            Bureau: '',
            Plan: '',
            Firmware: '',
            AccountNo: '',
            KeepActive: true,
            SimId: '',
            APN: '',
            SimType: '',
            MobileNo: '',
            SimNo: '',
            IP: '',
            DateEdited: new Date,
            DateLoaded: new Date,
            UserId: 0
        };
        return [dummyUnit];
    }
}


// WEBPACK FOOTER //
// ./angularApp/app/tools/report-company-unit/report-unit-background.service.ts